import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  IconButton,
  Drawer
} from '@mui/material';
import { Person, Lock, Logout, Refresh } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SessionManager } from '../utils/sessionManager';
import { authAPI } from '../services/api';
import './Layout.css';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = SessionManager.getUser();
  const isAdmin = user?.role === 'Admin';
  const [reportsOpen, setReportsOpen] = useState(false);
  
  // Avatar menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Logout confirmation dialog
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  
  // Change password dialogs
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [newPasswordDialogOpen, setNewPasswordDialogOpen] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Refs for OTP inputs
  const otpRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null)
  ];

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      // console.error('Logout error:', error);
      SessionManager.clearSession();
      navigate('/');
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleChangePasswordClick = () => {
    handleMenuClose();
    setChangePasswordDialogOpen(true);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    handleLogout();
  };

  const handleSendPasswordOTP = async () => {
    setLoading(true);
    try {
      const response = await authAPI.sendPasswordResetOTP({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      });
      
      if (response.success) {
        toast.success('OTP sent to your email!');
        setChangePasswordDialogOpen(false);
        setOtpDialogOpen(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.verifyPasswordResetOTP({
        email: user.email,
        otp: otpString
      });
      
      if (response.success) {
        toast.success('OTP verified! Enter your new password');
        setOtpDialogOpen(false);
        setNewPasswordDialogOpen(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 6 || password.length > 14) {
      errors.push('Password must be 6-14 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least 1 capital letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least 1 number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least 1 special character');
    }
    
    return errors;
  };

  const handleChangePassword = async () => {
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      passwordErrors.forEach(error => toast.error(error));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword({
        email: user.email,
        newPassword
      });
      
      if (response.success) {
        toast.success('Password changed successfully!');
        setNewPasswordDialogOpen(false);
        setNewPassword('');
        setConfirmNewPassword('');
        setOtp(['', '', '', '']);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOTPDialog = () => {
    setOtpDialogOpen(false);
    setOtp(['', '', '', '']);
  };

  const handleCloseNewPasswordDialog = () => {
    setNewPasswordDialogOpen(false);
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isSubUser = user?.role === 'SubUser';

  // Admin menu items
  const adminMenuItems = [
    { path: '/admin', icon: '📊', label: 'Dashboard' },
    { path: '/admin/pending-users', icon: '⏳', label: 'Pending Users' },
    { path: '/add-user', icon: '➕', label: 'Add User' },
    { path: '/all-clients', icon: '👥', label: 'All Clients' },
    { path: '/admin/add-credits', icon: '💳', label: 'Add Credits' },
    { path: '/kyc-verification', icon: '✓', label: 'KYC Verification' },
    { path: '/admin/api-pricing', icon: '💰', label: 'API Pricing' },
    { path: '/api-docs', icon: '📘', label: 'API Docs', external: true },
  ];

  // Client menu items (removed Profile from sidebar)
  const clientMenuItems = [
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/kyc-verification', icon: '✓', label: 'KYC Verification' },
    ...(isSubUser ? [] : [{ path: '/all-user', icon: '👥', label: 'Sub Users' }]), // Renamed for clients, hidden for sub-users
    ...(isSubUser ? [] : [{ path: '/buy-credits', icon: '🛒', label: 'Buy Credits' }]),
    { path: '/pricing', icon: '💰', label: 'Pricing' },
    { path: '/api-docs', icon: '📘', label: 'API Docs', external: true },
  ];

  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  return (
    <div className="app-layout">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {/* Desktop Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="sidebar-logo">
            KYC SUITE
          </Link>
          <div className="sidebar-subtitle">
            {isAdmin ? 'ADMIN PANEL' : 'KYC PLATFORM'}
          </div>
        </div>

        <div className="sidebar-menu">
          {menuItems.map((item) => (
            item.external ? (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="menu-item"
              >
                <span className="menu-item-icon">{item.icon}</span>
                <span className="menu-item-text">{item.label}</span>
              </a>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <span className="menu-item-icon">{item.icon}</span>
                <span className="menu-item-text">{item.label}</span>
              </Link>
            )
          ))}

          {/* Reports submenu for both admin and clients */}
          <>
            <div className="menu-item" onClick={() => setReportsOpen(!reportsOpen)}>
              <span className="menu-item-icon">📄</span>
              <span className="menu-item-text">Reports</span>
              <span className="menu-item-arrow">{reportsOpen ? '▼' : '▶'}</span>
            </div>

            {reportsOpen && (
              <div className="submenu">
                <Link
                  to="/reports"
                  className={`menu-item ${isActive('/reports') ? 'active' : ''}`}
                >
                  <span className="menu-item-text">Api Call List Summary</span>
                </Link>
                {/* Hidden - Temporarily disabled
                <Link
                  to="/subscription-history"
                  className={`menu-item ${isActive('/subscription-history') ? 'active' : ''}`}
                >
                  <span className="menu-item-text">Subscription History</span>
                </Link>
                */}
              </div>
            )}
          </>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: '#1a1f36',
            boxSizing: 'border-box'
          }
        }}
        ModalProps={{
          keepMounted: true // Better open performance on mobile
        }}
      >
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
          <div className="sidebar-header">
            <Link 
              to={isAdmin ? '/admin' : '/dashboard'} 
              className="sidebar-logo"
              onClick={toggleMobileMenu}
            >
              KYC SUITE
            </Link>
            <div className="sidebar-subtitle">
              {isAdmin ? 'ADMIN PANEL' : 'KYC PLATFORM'}
            </div>
          </div>

          <div className="sidebar-menu">
            {menuItems.map((item) => (
              item.external ? (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-item"
                  onClick={toggleMobileMenu}
                >
                  <span className="menu-item-icon">{item.icon}</span>
                  <span className="menu-item-text">{item.label}</span>
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={toggleMobileMenu}
                >
                  <span className="menu-item-icon">{item.icon}</span>
                  <span className="menu-item-text">{item.label}</span>
                </Link>
              )
            ))}

            {/* Reports submenu for both admin and clients */}
            <>
              <div className="menu-item" onClick={() => setReportsOpen(!reportsOpen)}>
                <span className="menu-item-icon">📄</span>
                <span className="menu-item-text">Reports</span>
                <span className="menu-item-arrow">{reportsOpen ? '▼' : '▶'}</span>
              </div>

              {reportsOpen && (
                <div className="submenu">
                  <Link
                    to="/reports"
                    className={`menu-item ${isActive('/reports') ? 'active' : ''}`}
                    onClick={toggleMobileMenu}
                  >
                    <span className="menu-item-text">Api Call List Summary</span>
                  </Link>
                </div>
              )}
            </>
          </div>
        </Box>
      </Drawer>

      {/* Avatar Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200
          }
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChangePasswordClick}>
          <ListItemIcon>
            <Lock fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Password</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmLogout} variant="contained" color="error">
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Confirmation Dialog */}
      <Dialog open={changePasswordDialogOpen} onClose={() => setChangePasswordDialogOpen(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Typography>
            We will send a 4-digit OTP to your email address: <strong>{user?.email}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendPasswordOTP} variant="contained" disabled={loading}>
            {loading ? 'Sending...' : 'Send OTP'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog open={otpDialogOpen} onClose={handleCloseOTPDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 1, fontWeight: 600, color: '#1976d2', fontSize: '1.5rem' }}>
          Verify OTP
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              We've sent a 4-digit OTP to
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
              {user?.email}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={otpRefs[index]}
                value={digit}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleOTPKeyDown(index, e)}
                inputProps={{
                  maxLength: 1,
                  style: { 
                    textAlign: 'center', 
                    fontSize: '32px', 
                    fontWeight: 600,
                    padding: '16px'
                  }
                }}
                sx={{
                  width: '64px',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderWidth: 2,
                      borderColor: digit ? '#1976d2' : '#ddd'
                    }
                  }
                }}
                autoFocus={index === 0}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            OTP expires in 10 minutes
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyOTP}
            disabled={loading || otp.some(digit => !digit)}
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={handleCloseOTPDialog}
            size="small"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Password Dialog */}
      <Dialog open={newPasswordDialogOpen} onClose={handleCloseNewPasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 1, fontWeight: 600, color: '#1976d2', fontSize: '1.5rem' }}>
          Set New Password
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="6-14 chars, 1 capital, 1 number, 1 special character"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleChangePassword}
            disabled={loading || !newPassword || !confirmNewPassword}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={handleCloseNewPasswordDialog}
            size="small"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Header */}
        <div className="top-header">
          {/* Mobile Header with Hamburger - Only show below 769px */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            alignItems: 'center', 
            gap: 2,
            flex: 1,
            '@media (min-width: 769px)': {
              display: 'none'
            }
          }}>
            <IconButton
              onClick={toggleMobileMenu}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <div style={{ flex: 1 }}>
              <div className="header-title" style={{ fontSize: '1rem' }}>
                {isAdmin ? 'Admin Dashboard' : 'KYC Verification Platform'}
              </div>
              <div className="header-subtitle" style={{ fontSize: '0.75rem' }}>
                {isAdmin ? 'Manage users, credits, and system settings' : 'Secure verification solutions'}
              </div>
            </div>
          </Box>

          {/* Desktop Header - Show from 769px and up */}
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            '@media (min-width: 769px)': {
              display: 'block'
            }
          }}>
            <div className="header-title">
              {isAdmin ? 'Admin Dashboard' : 'KYC Verification Platform'}
            </div>
            <div className="header-subtitle">
              {isAdmin ? 'Manage users, credits, and system settings' : 'Secure verification solutions'}
            </div>
          </Box>

          <div className="header-actions">
            {/* User Avatar and Menu */}
            <div 
              onClick={handleAvatarClick}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '8px',
                transition: 'background 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ 
                textAlign: 'right',
                display: window.innerWidth < 768 ? 'none' : 'block'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#2c3e50' }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  {user?.role}
                </div>
              </div>
              <Avatar 
                sx={{ 
                  bgcolor: '#1976d2', 
                  width: 40, 
                  height: 40,
                  fontSize: '16px',
                  fontWeight: 600
                }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="content-area">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
